import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { Message } from 'primereact/message';
import globalSettingsBranches from '../../../../../stores/global-settings-branches';
import globalUserPage from '../../../../../stores/global-user-page';
import appApi from '../../../../../api/app-api';
import { toast } from 'react-toastify';
import { showPermissionConfirmationDialog } from './permission-confirmation-dialog';

interface UserPagePermissionsProps {
  userId: number;
}

export const UserPagePermissions: React.FC<UserPagePermissionsProps> = observer(
  ({ userId }) => {
    const [loadingPermissionId, setLoadingPermissionId] = useState<
      number | null
    >(null);

    const branches = globalSettingsBranches.branches;
    const permissions = globalUserPage.administrationDetails?.permissions || [];

    const getBranchPermission = (branchId: number) => {
      return permissions.find((p) => p.branchId === branchId);
    };

    const handleAddPermission = async (
      branchId: number,
      branchName: string,
    ) => {
      showPermissionConfirmationDialog({
        branchName,
        isAddingPermission: true,
        onConfirm: async () => {
          setLoadingPermissionId(branchId);
          try {
            const response = await appApi.addUserPermission(userId, branchId);
            globalUserPage.administrationDetails = response.data.body;
            toast.success('הרשאה נוספה בהצלחה');
          } catch (error) {
            console.error('Error adding permission:', error);
            toast.error('שגיאה בהוספת הרשאה');
          } finally {
            setLoadingPermissionId(null);
          }
        },
      });
    };

    const handleRemovePermission = async (
      branchId: number,
      permissionId: number,
      branchName: string,
    ) => {
      showPermissionConfirmationDialog({
        branchName,
        isAddingPermission: false,
        onConfirm: async () => {
          setLoadingPermissionId(branchId);
          try {
            const response = await appApi.removeUserPermission(
              userId,
              permissionId,
            );
            globalUserPage.administrationDetails = response.data.body;
            toast.success('הרשאה הוסרה בהצלחה');
          } catch (error) {
            console.error('Error removing permission:', error);
            toast.error('שגיאה בהסרת הרשאה');
          } finally {
            setLoadingPermissionId(null);
          }
        },
      });
    };

    const branchNameTemplate = (rowData: any) => {
      return <span style={{ fontWeight: 'bold' }}>{rowData.branchName}</span>;
    };

    const permissionStatusTemplate = (rowData: any) => {
      const permission = getBranchPermission(rowData.id);
      if (permission) {
        return (
          <Tag
            style={{ textAlign: 'right' }}
            severity="success"
            value="יש הרשאה"
            icon="pi pi-check"
          />
        );
      }
      return (
        <Tag
          style={{ alignSelf: 'right' }}
          severity="danger"
          value="אין הרשאה"
          icon="pi pi-times"
        />
      );
    };

    const actionTemplate = (rowData: any) => {
      const permission = getBranchPermission(rowData.id);
      const isLoading = loadingPermissionId === rowData.id;

      if (permission) {
        return (
          <Button
            label="הסר הרשאה"
            icon="pi pi-trash"
            severity="danger"
            size="small"
            loading={isLoading}
            onClick={() =>
              handleRemovePermission(
                rowData.id,
                permission.id,
                rowData.branchName,
              )
            }
          />
        );
      }

      return (
        <Button
          label="הוסף הרשאה"
          icon="pi pi-plus"
          severity="success"
          size="small"
          loading={isLoading}
          onClick={() => handleAddPermission(rowData.id, rowData.branchName)}
        />
      );
    };

    return (
      <div style={{ direction: 'rtl', padding: '20px' }}>
        <ConfirmDialog />
        <div style={{ marginBottom: '20px' }}>
          <h2 style={{ margin: '0 0 10px 0', fontSize: '1.5rem' }}>
            ניהול הרשאות סניפים
          </h2>
          <p style={{ color: '#666', margin: '0 0 20px 0' }}>
            נהל את הרשאות המשתמש לסניפים השונים
          </p>

          <Message
            severity="info"
            style={{
              marginBottom: '20px',
              width: '100%',
              textAlign: 'right',
              direction: 'rtl',
            }}
            content={
              <div style={{ lineHeight: '1.6' }}>
                <p style={{ margin: '0 0 10px 0', fontWeight: 'bold' }}>
                  שים לב,
                </p>
                <p style={{ margin: '0 0 8px 0' }}>
                  בעמוד זה ניתן לסמן הרשאות מתאימות לכלל השלוחות.
                </p>
                <p style={{ margin: '0 0 8px 0' }}>
                  כאשר למשתמש שנבחר תהיה הרשאה לשלוחה, הוא יוכל לצפות בכלל
                  הנתונים והדוחות (פיננסים, לימודיים וחשבונאיים) של אותה השלוחה,
                  וגם לתפריט הצד בסרגל מצד ימין.
                </p>
                <p style={{ margin: 0, color: '#0066cc', fontWeight: '500' }}>
                  חשוב לדעת: בכל רגע תוכל לשנות את הרשאות הגישה לשלוחות.
                </p>
              </div>
            }
          />
        </div>

        <DataTable
          value={branches}
          dataKey="id"
          emptyMessage="לא נמצאו סניפים"
          style={{ direction: 'rtl' }}
        >
          <Column
            field="branchName"
            header="שם הסניף"
            body={branchNameTemplate}
            style={{ textAlign: 'right' }}
          />
          <Column
            header="סטטוס הרשאה"
            body={(rowData) => (
              <div style={{ display: 'flex', justifyContent: 'right' }}>
                {permissionStatusTemplate(rowData)}
              </div>
            )}
            style={{ textAlign: 'center', width: '200px' }}
          />
          <Column
            header="פעולות"
            body={(rowData) => (
              <div style={{ display: 'flex', justifyContent: 'right' }}>
                {actionTemplate(rowData)}
              </div>
            )}
            style={{ textAlign: 'center', width: '200px' }}
          />
        </DataTable>
      </div>
    );
  },
);
